import React from 'react';
import { 
  Typography, 
  Box, 
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
  Button,
  Stack
} from '@mui/material';
import { 
  LocationOn, 
  ArrowForward,
  CalendarMonth,
  InsertPhoto as InsertPhotoIcon,
  Attachment as AttachmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { getDisplayStatus } from '../../utils/serviceRequestUtils';
import { Bid, formatDate } from 'shared-lib';
import { 
  getBidStatusLabel, 
  getBidStatusColor, 
  getStatusColor, 
  getStatusBackground
} from '../../utils/serviceRequestUtils';

interface ServiceRequestCardProps {
  request: any;
  bidStatus: Bid | null;
  onViewRequest: (requestId: string) => void;
}

const ServiceRequestCard: React.FC<ServiceRequestCardProps> = ({ 
  request, 
  bidStatus, 
  onViewRequest 
}) => {
  const currentStatus = getDisplayStatus(bidStatus);
  
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '16px',
        border: '1px solid rgba(16, 29, 59, 0.2)',
        boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0px 16px 48px -8px rgba(16, 29, 59, 0.15)'
        }
      }}
    >
      <CardContent sx={{ pb: 1, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              sx={{ 
                mr: 1.5, 
                bgcolor: '#00AB9B',
                color: 'white',
                width: 36,
                height: 36
              }}
            >
              {request.serviceName?.[0] || 'S'}
            </Avatar>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 700, color: '#101D3B' }}>
                {request.serviceName || 'Service Request'}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mr: 2 }}>
                  <LocationOn fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.54)' }} />
                  {`${request.city}, ${request.state}`}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarMonth sx={{ fontSize: '0.875rem', color: 'rgba(16, 29, 59, 0.6)', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: 'rgba(16, 29, 59, 0.6)' }}>
                  {formatDate(request.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Chip 
            label={getBidStatusLabel(bidStatus)} 
            color={getBidStatusColor(bidStatus) as any}
            size="small"
            sx={{ 
              height: '24px',
              borderRadius: '12px',
              bgcolor: getStatusBackground(bidStatus),
              color: getStatusColor(bidStatus),
              fontWeight: 500,
              '& .MuiChip-label': {
                padding: '0 8px'
              }
            }}
          />
        </Box>
        
        {/* Show bidding end date for no-bid requests */}
        {currentStatus === 'no-bid' && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1, 
            mb: 1,
            color: '#f57c00',
            border: '1px solid rgba(245, 124, 0, 0.5)',
            borderRadius: '8px',
            py: 0.5,
            px: 1,
            fontSize: '0.8rem',
            backgroundColor: 'rgba(245, 124, 0, 0.05)'
          }}>
            <AccessTimeIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Bidding closes: {request.biddingEndDate ? formatDate(request.biddingEndDate) : 'Not specified'}
            </Typography>
          </Box>
        )}
        
        {/* Request status indicator - show for closed or removed requests */}
        {(request.status === 'closed' || request.status === 'removed') && (
          <Chip
            label={request.status === 'closed' ? 'Closed' : 'Removed'}
            size="small"
            color={request.status === 'closed' ? 'success' : 'error'}
            sx={{ 
              mt: 1,
              height: '24px',
              opacity: 0.85,
              fontWeight: 500
            }}
          />
        )}
        
        {/* Won indicator - show when vendor's bid is accepted */}
        {getDisplayStatus(bidStatus) === 'accepted' && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            color: '#00AB9B',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}>
            <CheckCircleIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
            You won this bid!
          </Box>
        )}
        
        {/* Rejected indicator - show when vendor's bid is rejected */}
        {getDisplayStatus(bidStatus) === 'rejected' && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            color: '#d32f2f',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}>
            <CancelIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
            Client selected another vendor
          </Box>
        )}
        
        <Divider sx={{ my: 1.5, borderColor: 'rgba(16, 29, 59, 0.1)' }} />
        
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Schedule sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.25rem' }} />
            <Typography variant="body2" sx={{ color: '#101D3B' }}>
              {request.projectTimeline || 'Timeline not specified'}
              {request.preferredDate && ` (${formatDate(request.preferredDate)})`}
            </Typography>
          </Box>
          
          {/* Attachments indicator */}
          {(() => {
            const detailsAny = request.serviceDetails as any;
            const hasAttachments = detailsAny?.attachments && Array.isArray(detailsAny.attachments) && detailsAny.attachments.length > 0;
            const hasPhotoUrls = detailsAny?.photoUrls && Array.isArray(detailsAny.photoUrls) && detailsAny.photoUrls.length > 0;
            
            // Calculate counts
            const attachmentCount = hasAttachments ? detailsAny.attachments.length : (hasPhotoUrls ? detailsAny.photoUrls.length : 0);
            
            // Check if there are non-image attachments
            const hasNonImageAttachments = hasAttachments && 
              detailsAny.attachments.some((att: any) => !att.fileType?.startsWith('image/'));
            
            if (attachmentCount > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {hasNonImageAttachments ? (
                    <AttachmentIcon fontSize="small" sx={{ mr: 0.5, color: 'rgba(16, 29, 59, 0.6)' }} />
                  ) : (
                    <InsertPhotoIcon fontSize="small" sx={{ mr: 0.5, color: 'rgba(16, 29, 59, 0.6)' }} />
                  )}
                  <Typography variant="body2" color="textSecondary">
                    {attachmentCount} attachment{attachmentCount !== 1 ? 's' : ''} attached
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}
        </Stack>
      </CardContent>
      
      <Box sx={{ px: 2, pb: 2, mt: 'auto' }}>
        <Button 
          variant="contained" 
          fullWidth
          onClick={() => onViewRequest(request.id)}
          endIcon={<ArrowForward />}
          sx={{ 
            borderRadius: '5px',
            py: 1,
            bgcolor: '#00AB9B',
            color: 'white',
            transition: 'background-color 0.3s ease',
            '&:hover': {
              bgcolor: '#00AB9B',
              filter: 'brightness(0.9)'
            }
          }}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
};

export default ServiceRequestCard; 